// ============================================================================
// 组件层契约常量（跨端规范 docs/superpowers/specs/2026-08-09-cross-platform-ui-spec.md §8.2）
//
// 【这份文件解决什么】结构 token（radius/spacing/shadow/motion）三端早就逐字段
// 相同、各有金样锁死 —— 原子层是干净的。漂移全部落在**原子之上一层**：三端都
// 用 radius-md，可是「气泡内边距多少」「embed 加载时显示什么」「KB 正文 h1 多大」
// 从来没被写下来过，于是三端各自发明。
//
//   token 保证「用同一把尺子」，不保证「量出同一件衣服」。这份文件定衣服。
//
// 【消费方】
//   - web：本模块直接被 widget/chat/know 各处 import（唯一真源，禁止再抄字面量）
//   - Android / iOS：读 sdk/shared/design-tokens.json 的 `components` 段
//     （由 scripts/export-design-tokens.mjs 从本模块导出），各写一条金样断言
//   任一端漂移即红 —— 与既有 StructureTokensTest / structure-tokens.test.mjs
//   同一门派，复用同一条管线，不另起炉灶。
//
// 【纪律】
//   1. 改本文件 = 改三端组件契约：必须重跑 `npm run tokens:export` 并提交
//      sdk/shared/design-tokens.json 的 diff；diff 非空即要求三端同步 PR。
//   2. 只放**规范 §6.1「必须一致」**的值。允许平台差异的（触控反馈形式、滚动
//      物理、字体家族、图标绘制方式、桌面 web 输入框字号…）**不进这张表** ——
//      放进来会让另两端的金样为了一个本就该不同的值而红。
//   3. 零依赖纯数据模块：能被 `node` 直接加载（导出脚本要 import 它），不许
//      引入 React / DOM / 任何运行时副作用。
// ============================================================================

/**
 * 消息气泡（规范 §3.1）。
 * 纵向 10 与横向 12 的不对称是刻意的：横向 12 = space-3（裁决 R2，14 是 off-scale
 * 历史残留）；纵向 10 不在标尺上但**不动** —— 改它会改变气泡高度，牵动列表的
 * 行高缓存与滚动锚点（裁决 R2 的附注）。
 */
export const BUBBLE = Object.freeze({
    padVPx: 10,
    padHPx: 12,
    minWidthPx: 50,
    maxWidthPct: 80,
    avatarSizePx: 36,
    avatarGapPx: 10,
    /** 无头像时的占位宽 = avatarSize + avatarGap，保证有无头像两行左边缘对齐 */
    avatarSpacerPx: 46,
    agentNameSizePx: 13,
    /** 规范 §3.1：客服名下间距 space-1。web 曾写 3px（off-scale） */
    agentNameGapPx: 4,
    timeSizePx: 11,
    timeGapPx: 4,
    /** 同一发送者连续消息 / 跨发送者分组 的间距 */
    groupGapPx: 4,
    crossGroupGapPx: 12,
    /** 入场：translateY → 0 + 淡入，dur-slow。仅真·尾部追加时播 */
    enterTranslateYPx: 12,
    /** 按下反馈缩放（web/iOS scale，Android 可用 ripple 等效，规范 §6.2） */
    pressScale: 0.97,
    /** 长按：预触觉 → 触发菜单 */
    longPressHapticMs: 480,
    longPressTriggerMs: 500,
});

/** 消息列表（规范 §3.2）。全部是判据/阈值类常量，三端零容忍。 */
export const LIST = Object.freeze({
    contentPaddingHPx: 16,
    /** 裁决 R1：首条上方与末条下方各 16 的**视觉留白**。reverseLayout 端用等效手段实现 */
    contentPaddingVPx: 16,
    stickToBottomPx: 8,
    jumpButtonPx: 120,
    followWindowMs: 800,
    pagingTriggerPx: 100,
    memoryThrottleMs: 250,
    highlightMs: 2000,
});

/**
 * typing 指示器（规范 §3.2）。
 * **静态文本三点不合规** —— Android 现为 15sp 的 "···"，iOS 无槽位。
 */
export const TYPING = Object.freeze({
    dotSizePx: 7,
    dotGapPx: 4,
    cycleMs: 1400,
    delaysMs: Object.freeze([0, 160, 320]),
    bounceTranslateYPx: -6,
    bubblePadVPx: 11,
    bubblePadHPx: 14,
});

/**
 * header 三档（规范 §3.3）。高度公式三端已一致，此处固化，不得再各自计算。
 * rowMetrics：`[行高, 行下间距]`，按「有工具钮 / 仅头像 / 都无」三种情形取。
 */
export const HEADER = Object.freeze({
    largeHPx: 130,
    largeHWithTabsPx: 160,
    compactHPx: 72,
    tabRowHPx: 40,
    tabGapPx: 8,
    rowMetrics: Object.freeze([
        Object.freeze([40, 8]),
        Object.freeze([36, 8]),
        Object.freeze([32, 6]),
    ]),
    patternAlpha: 0.075,
    patternTileCount: 16,
    /** Home 视图下 header 与欢迎区共用的虚拟渐变盒高度（不随滚动变） */
    heroBoxHPx: 320,
    avatarLargePx: 36,
    avatarSmallPx: 28,
    overlapLargePx: -10,
    overlapSmallPx: -8,
    /** 副行不透明度：统一 0.7（取消 web 的 0.75，规范 §3.3） */
    subtitleOpacity: 0.7,
    iconButtonPx: 32,
    iconButtonGlyphPx: 16,
    tabWUnderlinePx: 96,
    indicatorWPx: 28,
    indicatorHPx: 3,
    popoverSettingsWPx: 190,
    popoverLangWPx: 246,
});

/**
 * composer（规范 §3.4）。
 * 刻意**不含**工具按钮的视觉尺寸：裁决 R6 明确三端各保持现状视觉
 * （web 36 / Android 28 / iOS 32），只要求命中区外扩到 44。把视觉尺寸写进
 * 「必须一致」表会让另两端的金样为一个本就允许差异的值而红。
 */
export const COMPOSER = Object.freeze({
    lineHeightPx: 18,
    minHeightPx: 38,
    /** 5 行封顶。web 的 CSS 里另有 162/180 两个更大的值，JS 封顶后不可达 */
    maxHeightPx: 90,
    maxRows: 5,
    sendButtonPx: 36,
    sendIconPx: 15,
    /** 裁决 R3：移动端三端统一 16（web 桌面 14 是登记在案的平台豁免，不进本表） */
    inputFontSizePx: 16,
    minTouchTargetPx: 44,
    minTouchGapPx: 8,
    panelHeightPx: 240,
    gifTileWPx: 64,
    gifTileHPx: 52,
    gifGapPx: 6,
    voiceMaxSeconds: 60,
    voiceWaveSlots: 24,
});

// embed 容器（规范 §5）的常量**不在本文件**：它们的真源是
// `src/chat/embed-message.js` 里那批 `export const EMBED_*`（协议 clamp、超时、
// 骨架延迟、回执停留、并发预算 2、懒加载预取 200px…），导出脚本从那里正则抽取
// 进 design-tokens.json 的 `constants.embed`，并镜像一份到 `components.embed`
// 供三端按同一路径读取。
//
// 为什么不在这里再抄一份：抄的那一刻就有了两个真源。改并发预算只该改
// embed-message.js 一处，然后重跑 `npm run tokens:export`。
//
// ⚠️ 已知风险（登记在案）：web 的 embed 渲染器实际上只被测试与投票 demo 走到，
// 没有真实流量 —— 它会静默腐烂而没人发现。design-tokens 的 components.embed
// 金样是目前唯一持续盯着这批常量的东西。

/** 富消息卡（规范 §4.3）。三套圆角/四套宽度/三套边框收敛后的唯一一套。 */
export const CARD = Object.freeze({
    maxWidthPx: 320,
    carouselItemWPx: 220,
    carouselGapPx: 8,
    imageRatioDefault: '16:9',
    titleSizePx: 15,
    subtitleSizePx: 13,
    descSizePx: 13,
    descLineHeight: 1.45,
    ratingStarPx: 26,
    ratingStarGapPx: 8,

    // ---- carousel 翻页指示点（规范 §4.3「必须有翻页可供性」，裁决 R10）------
    // 三端**一致地缺**这一块：桌面还有两端渐隐 + hover 箭头兜着，触屏什么都没有
    // ——卡片切齐在容器右边缘，看上去就是一张卡，右边还有两张这件事无从得知，
    // 轮播等于白做。补一排指示点，几何三端跟这张表，避免各画各的。
    //
    // 定形取舍：
    //  · **纯指示，不可点**。可点就要吃 44 命中区（§2.3），一排 6px 的点要撑到
    //    44 意味着彼此间距被撑开到不成一排；而它要表达的是"还有几张"，翻页本身
    //    有滑动/箭头两条路了。aria-hidden：位置信息对读屏没有意义，卡片本身可
    //    被逐个读到。
    //  · **只在 ≥2 张时出现**。一张卡配一个点是纯噪音。
    //  · 激活点用 mainColor，未激活用 surface-3 —— 与 rating 星的选中/未选同一
    //    对 token，卡内两处"进度感"的表达方式保持一致。
    //  · 只换色不换尺寸：宽度动画会让整排点横向抖动，而它就贴在卡下面。
    dotPx: 6,
    dotGapPx: 4,            // space-1：同一元素内部
    dotsMarginTopPx: 8,     // space-2：与卡片同组
    dotsMinCards: 2,
    dotActiveColorToken: '--lc-main',
    dotIdleColorToken: '--lc-surface-3',
});

/**
 * KB 文章正文排版表（规范 §4.2，审计 C9）。
 *
 * 【为什么是一张表】现状三端各写各的：web 是 `dangerouslySetInnerHTML` 裸注入
 * **一条标签规则都没有**（h1 和正文一样大、代码块没有底、表格没有边框），
 * Android 注入了较完整的一份 CSS，iOS 注入了另一份（lh 1.6 / padding 16）。
 * 同一篇文章在三端读起来是三个排版。→ 收敛到本表，三端注入同一份。
 *
 * 【三端怎么用】
 *   web：`kbArticleCssText()` 生成 <style>，注入 KnowledgeDetail 的正文容器
 *   Android / iOS：读 design-tokens.json 的 `components.kbArticle`，
 *                  各自拼 WebView 注入的 CSS（值必须逐字段等于本表）
 *
 * 【单位约定】`*Px` 是 CSS px（native 侧 WebView 里同样是 CSS px，不做 dp 换算 ——
 * WebView 内部就是 web 排版引擎，换算才会漂）。颜色一律给语义 token 名，不给
 * 字面量：native 注入前把 token 名替换成当前主题的实际值。
 */
export const KB_ARTICLE = Object.freeze({
    /** 正文容器 */
    paddingPx: 20,
    bodySizePx: 15,
    bodyLineHeight: 1.8,
    bodyColorToken: '--lc-text',

    h1SizePx: 24,
    h1Weight: 700,
    h1LineHeight: 1.4,
    h1MarginBottomPx: 8,      // space-2
    h1MarginTopPx: 24,        // space-6（首个 h1 由 web 侧 :first-child 归零）

    h2SizePx: 18,
    h2Weight: 600,
    h2LineHeight: 1.4,
    h2MarginTopPx: 24,        // space-6
    h2MarginBottomPx: 8,      // space-2

    h3SizePx: 15,
    h3Weight: 600,
    h3LineHeight: 1.45,
    h3MarginTopPx: 16,        // space-4
    h3MarginBottomPx: 4,      // space-1

    pMarginBottomPx: 12,      // space-3
    listIndentPx: 24,         // space-6
    listItemGapPx: 4,         // space-1

    linkColorToken: '--lc-main',
    linkUnderline: true,

    /** 分类徽标（正文上方 meta 行） */
    badgePadVPx: 4,
    badgePadHPx: 10,
    badgeRadiusPx: 4,         // radius-xs
    badgeSizePx: 12,
    badgeWeight: 500,
    badgeBgToken: '--lc-main-tint',
    badgeColorToken: '--lc-main',

    /** tag 胶囊（正文下方 tags 区） */
    tagPadVPx: 4,
    tagPadHPx: 12,
    tagRadiusPx: 12,          // radius-md
    tagSizePx: 12,
    tagBgToken: '--lc-surface-2',
    tagColorToken: '--lc-text-2',
    tagsMarginTopPx: 24,      // space-6
    tagsPaddingTopPx: 24,     // space-6
    tagsGapPx: 8,             // space-2

    /** 代码 */
    codeBgToken: '--lc-surface-2',
    codeRadiusPx: 8,          // radius-sm
    codePaddingPx: 12,        // space-3
    codeSizeEm: 0.9,

    /** 引用块 */
    blockquoteBarPx: 3,
    blockquotePadHPx: 12,     // space-3
    blockquoteColorToken: '--lc-text-2',

    /** 表格 */
    tableFontSizePx: 13,
    tableCellPadVPx: 4,
    tableCellPadHPx: 8,
    tableBorderToken: '--lc-border',

    /** 图片 */
    imageRadiusPx: 8,         // radius-sm
    imageMaxWidthPct: 100,
});

/** KB 列表与搜索（规范 §4.2） */
export const KB = Object.freeze({
    nestedThreshold: 5,
    searchDebounceMs: 500,
    searchFontSizePx: 14,
    rowPadVPx: 13,
    rowPadHPx: 16,
    rowTitleSizePx: 14.5,
    rowSummarySizePx: 13,
    rowSummaryLineHeight: 1.5,
    summaryChars: 120,
    iconTrayPx: 34,
    iconTrayGlyphPx: 16,
    chevronPx: 16,
    backButtonPx: 32,
    backGlyphPx: 16,
    /** 分类胶囊是**胶囊**：radius-full，不是恰好等于 16 的字面量（规范 §1.2 第 4 条） */
    categoryPillRadius: 'full',
    categoryPillPadVPx: 6,
    categoryPillPadHPx: 12,
    categoryPillSizePx: 13,
});

/** Home 视图（规范 §4.1） */
export const HOME = Object.freeze({
    heroPadHPx: 20,
    heroPadTopPx: 12,
    heroPadBottomPx: 14,
    heroGapPx: 6,
    titleSizePx: 18,
    statusDotPx: 7,
    statusTextSizePx: 13,
    contentPaddingPx: 16,
    contentGapPx: 12,
    rowPadVPx: 12,
    rowPadHPx: 16,
    iconTrayPx: 40,
    iconTrayGlyphPx: 20,
    iconTrayAlpha: 0.082,
    summaryChars: 40,
    searchFontSizePx: 13,
    linkCardMax: 5,
    linkCardHPx: 68,
    seeAllPadVPx: 11,
    seeAllChevronPx: 14,
});

/** WebView 壳 chrome（规范 §7）。native 在 widget 加载前后自己画的那一层。 */
export const SHELL = Object.freeze({
    skeletonHeaderHPx: 72,
    skeletonTimeoutMs: 6000,
    errorIconPx: 40,
    errorTitleSizePx: 15,
    errorBodySizePx: 13,
    errorBodyMaxWPx: 280,
});

/**
 * 启动器 / 悬浮按钮（规范 §2.1）。**面板之外**那颗按钮，以及骑在它上面的
 * 未读角标。
 *
 * 【为什么补这一组】结构 token 早就三端一致（radius-full / shadow-2 / dur-slow /
 * ease-spring），可是「hover 放多大」「按下缩多少」「挂载从哪个 scale 起跳」
 * 「角标多高、探出去几 px、几位数截断」从来没有被写下来过 —— macOS 那批
 * (ce57353) 只能把这些值**抄**进 `LaneChatLauncherTokens.Interaction` / `.Badge`，
 * 注释里老实写着「web 有、共享表还没有」。抄写就是漂移的开始：下次谁改了 web 的
 * hover 缩放，另两端不会红、也不会有人知道。
 *
 * 【真值来源】一律以 **web** 为准 —— 它是真实访客看到的那颗按钮：
 *   `src/widget/chat-floating-button.js`（交互态、角标、图标比例、尺寸钳位）
 *   `src/widget/style.js` 的 `mobileTitleStyle`（直径、圆角、阴影、过渡）
 *   `src/widget/default-configuration.js` 的 `btnPcRight` / `btnPcBottom`（边距）
 * 服务端 `SdkController::launcherConfig` 的钳位区间（40–100 / 0–200）同步登记在
 * 此，三端不要各钳各的。
 *
 * 【两处登记在案的偏离，故意保留而不"取中间值"】
 *   1. `badgeBgColor` 是 `#ef4444`，**不是** `--lc-danger`（`#e53e3e`）。角标红
 *      与"发送失败红"历史上就是两个值；统一它是一次像素变更，不是一次契约提取，
 *      要改先走设计裁决。
 *   2. hover 抬一档（shadow-2 → shadow-3）。**裁决 2026-08-09**：此前 web 的
 *      transition 里列着 box-shadow 却没有任何一处改写它——那条过渡是死的，
 *      而"声明了却没实现"本身就是意图的证据；macOS 那边已经抬了
 *      （`LaneChatLauncherButton.applyShadow`）。两端取"抬"而不是"都不抬"：
 *      悬停给高程反馈是这类浮层按钮的通行做法，且它让那条过渡真正生效，
 *      而不是留一段永远不触发的 CSS 骗后人。web 跟上 macOS，不是反过来。
 */
/**
 * 双色（duotone）图标两层的不透明度。**同一个前景色的两个 alpha，不是第二种
 * 颜色** —— 这条是整个双色方案成立的前提：
 *
 * 前景色由 `pickIconOn(主色)` 给出（3:1，WCAG 1.4.11 图形元素门槛）。若底层
 * 换成"另一种颜色"，那么每来一个新主色就要重新推导一次两色关系与各自的对比度；
 * 而两层同色不同 alpha 时，只要前景色对主色成立，底层就是"前景色以 20% 混进
 * 主色"——它落在主色与前景色之间，永远不会比两者更极端，任何主色都不用重算。
 *
 * 之所以是 0.2 而不是别的数：这是 Phosphor duotone 资源自带的 `opacity="0.2"`，
 * 图形的明暗层次是画的时候按这个值画的。改它等于改美术，不是改样式。
 */
const DUOTONE_BACK_OPACITY = 0.2;
const DUOTONE_FRONT_OPACITY = 1;
/**
 * 按下时底层抬到的不透明度（见 LAUNCHER.duotonePressBackOpacity 的说明）。
 * 0.2 → 0.34 是"图标被按实了一点"，不是换了个图标。
 */
const DUOTONE_PRESS_BACK_OPACITY = 0.34;

/**
 * 启动器图标的两种风格。**数据在这里，渲染在各端**——web 的 `<path>`、macOS 的
 * `NSBezierPath`（构建期由 `scripts/gen-swift-launcher-glyphs.mjs` 生成 Swift
 * 代码，**不做运行时 `d` 解析**：运行时解析器就是又一个会漂移的东西）。
 *
 * 【为什么这里可以放 `d` 字符串，而 `icon-set.js` 明令禁止】
 * icon-set 里的六个图标是我们自己的折线/圆/矩形，图元表达得下，所以用图元；
 * 这两枚启动器图标是**外部美术资源**（Material / Phosphor），贝塞尔曲线拆不成
 * 图元，硬拆就是我们重画一遍别人的图 —— 那既不是原资源，也丢了授权链条。
 * 于是改为：`d` 原样入库（可与上游逐字节对账），跨端消费靠构建期代码生成。
 *
 * 【为什么 duotone 有"体积感"而我们原来那枚没有】
 * 实测 Crisp 的按钮：54×54 纯色圆、`background-image: none`、阴影比我们的还淡
 * （`rgba(0,0,0,.05) 0 4px 10px`）、`svgCount: 0`。它的立体感**不来自按钮 CSS**，
 * 来自图标位图自带明暗层次。所以正解是换美术资源，不是往按钮上堆渐变/内阴影/
 * 描边去仿一个廉价的拟物按钮（Crisp 自己也没这么干）。duotone 天生两层：一层
 * 低透明度实心 + 一层实色轮廓与细节，层次来自形状本身。
 *
 * 【相对 Crisp 的优势】它那张位图钉死是蓝的；我们两层都吃 `pickIconOn(主色)`，
 * 商户换主色，图标跟着换。
 */
export const LAUNCHER_GLYPHS = Object.freeze({
    /**
     * 默认：Phosphor Icons `chat-teardrop-dots`（duotone 字重）。
     *
     * 【授权】MIT。LICENSE 全文入库在 `src/widget/vendor/phosphor-icons-LICENSE.txt`
     * （MIT 要求保留版权与许可声明）。只取这一枚图标的两条 `d`，**没有引入整包**，
     * 也没有加 npm 依赖 —— widget 是自包含 bundle、CSP 禁外链，为一枚图标带进
     * 一整套字形是我们一开始就拒绝过的事（见 icon-set.js 顶部）。
     * 上游：https://github.com/phosphor-icons/core `assets/duotone/chat-teardrop-dots-duotone.svg`（v2.1.1）
     *
     * 【为什么是 teardrop-dots 而不是 chat-dots】启动器是个**圆**。矩形气泡加一
     * 条细尾巴嵌在圆里，四角留下的负空间很别扭；teardrop 是"三圆角 + 一尖角"，
     * 轮廓本身贴着圆。尖角同样朝左下，与原 Material 气泡的尾巴同向，商户不会
     * 觉得换了个东西。
     */
    duotone: Object.freeze({
        grid: 256,
        /**
         * 不做视觉下移。Phosphor 这枚在自己网格上已近似光学居中：形心 y=124，
         * 网格中心 128，偏高 4/256 —— 折算到 24px 图标是 0.375px，低于渲染阈值。
         * （classic 需要下移是因为它的尾巴挂在左下、视觉重心明显偏高。）
         */
        padTopPx: 0,
        source: 'Phosphor Icons — chat-teardrop-dots (duotone weight), v2.1.1',
        sourceUrl: 'https://github.com/phosphor-icons/core',
        license: 'MIT',
        licenseFile: 'app/frontend/src/widget/vendor/phosphor-icons-LICENSE.txt',
        layers: Object.freeze([
            /** 底层：实心水滴形气泡，20% 不透明度 —— 图标的"体积"。 */
            Object.freeze({
                role: 'back',
                opacity: DUOTONE_BACK_OPACITY,
                d: 'M224,124h0a92,92,0,0,1-92,92H48a8,8,0,0,1-8-8V124a92,92,0,0,1,92-92h0A92,92,0,0,1,224,124Z',
            }),
            /** 面层：轮廓 + 三个点，实色 —— 图标的"形"，也是承担 3:1 的那一层。 */
            Object.freeze({
                role: 'front',
                opacity: DUOTONE_FRONT_OPACITY,
                d: 'M132,24A100.11,100.11,0,0,0,32,124v84a16,16,0,0,0,16,16h84a100,100,0,0,0,0-200Zm0,184H48V124a84,84,0,1,1,84,84Zm12-80a12,12,0,1,1-12-12A12,12,0,0,1,144,128Zm-44,0a12,12,0,1,1-12-12A12,12,0,0,1,100,128Zm88,0a12,12,0,1,1-12-12A12,12,0,0,1,188,128Z',
            }),
        ]),
    }),

    /**
     * 既有的 Material `chat` 气泡，**保留为可选项**（商户裁定）。这不是"要被替换
     * 掉的旧东西"，是两个选项里的一个：单层实心，观感更重更平，认得它的存量商户
     * 可以留着。
     *
     * 单条 `d`，三条横杠是反向绕行的子路径 —— 非零环绕规则下被"挖空"，所以图标
     * 在任何底色上都成立（不需要拿背景色去填那三道杠）。macOS 侧
     * `LaneChatBrandGlyph.launcherGlyphPath` 一直是这条路径的图元转写。
     */
    classic: Object.freeze({
        grid: 24,
        /**
         * flex 居中的盒子里，2 的上内边距把图元往下推 **1**（半个）—— 气泡尾巴
         * 挂在左下，不推的话视觉重心偏高。native 侧要的是位移量：
         * `padTopPx / 2 / diameterPx` 乘直径。
         */
        padTopPx: 2,
        source: 'Material Icons — chat',
        sourceUrl: 'https://github.com/google/material-design-icons',
        license: 'Apache-2.0',
        licenseFile: '',
        layers: Object.freeze([
            Object.freeze({
                role: 'front',
                opacity: DUOTONE_FRONT_OPACITY,
                d: 'M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z',
            }),
        ]),
    }),
});

export const LAUNCHER = Object.freeze({
    // ---- 几何：直径与位置（商户可在 dash 调，三端钳位区间必须一致）--------
    /** 出厂直径。`mobileTitleStyle` 56×56 = dash `WidgetConfig.vue` 默认值 */
    diameterPx: 56,
    minDiameterPx: 40,
    maxDiameterPx: 100,
    /** 出厂边距（`defaultConfiguration.btnPcRight` / `btnPcBottom`，均 20px） */
    marginRightPx: 20,
    marginBottomPx: 20,
    minMarginPx: 0,
    maxMarginPx: 200,
    /** 圆的方式是 radius-full 这档 token，不是本地 `/ 2`（规范 §1.2 第 4 条） */
    radiusToken: '--lc-radius-full',
    shadowToken: '--lc-shadow-2',
    /** 见表头偏离 2：hover 不换档 */
    hoverShadowToken: '--lc-shadow-3',

    // ---- 图标 ----------------------------------------------------------
    /** 在 `diameterPx` 下的图标边长；其它直径按 glyphPx/diameterPx 等比 */
    glyphPx: 24,
    /**
     * classic 图元的设计网格（macOS `Glyph.grid`）。**每种风格的网格现在写在
     * `LAUNCHER_GLYPHS[style].grid` 里**（duotone 是 256），这个字段留着是因为
     * macOS `LaneChatLauncherTokens.Glyph.grid` 与 design-tokens 的既有读者都
     * 按名字取它 —— 它的语义收窄成"classic 的网格"，不是"启动器的网格"。
     */
    glyphGridPx: 24,
    /**
     * classic 的视觉下移量，同上：语义收窄，真源是
     * `LAUNCHER_GLYPHS.classic.padTopPx`，这里是它的镜像。
     */
    glyphPadTopPx: 2,

    // ---- 图标风格（widget_config.launcher_icon_style）--------------------
    /**
     * 合法值域。服务端（`ApiController::sanitizeWidgetAppearance` /
     * `ApplicationController::normalizeLauncherIconStyle` /
     * `UsageController::start` / `SdkController::launcherConfig`）与 dash 的
     * 选择器都必须只认这两个值，脏值一律归一到 `defaultIconStyle`。
     */
    iconStyles: Object.freeze(['duotone', 'classic']),
    /**
     * 出厂风格。取 duotone：这是纯视觉改进、不改任何行为，而"体积感"正是这次
     * 要解决的问题——把新资源藏在一个默认关闭的开关后面，等于没解决。想留旧观感
     * 的商户在 dash 一键换回 classic，旧资源一行没删。
     */
    defaultIconStyle: 'duotone',
    /** duotone 底层不透明度（见 DUOTONE_BACK_OPACITY 处的完整说明） */
    duotoneBackOpacity: DUOTONE_BACK_OPACITY,
    /** duotone 面层不透明度 */
    duotoneFrontOpacity: DUOTONE_FRONT_OPACITY,
    /**
     * **按下**时底层抬到的不透明度。这是本次唯一新增的动效，理由见
     * `chat-floating-button.js` 里"任务 B 选路"那段注释：面板一打开按钮就整个
     * 不渲染了，"气泡形变成叉号"在当前结构下不成立；能做且值得做的是关闭态
     * 自身的按压反馈。它只动 `fill-opacity`（合成器属性，不触发重排），而且
     * **只有 duotone 有**——单层的 classic 没有第二层可抬，按下仍只有按钮缩放。
     */
    duotonePressBackOpacity: DUOTONE_PRESS_BACK_OPACITY,

    // ---- 交互三态（规范 §6.2）------------------------------------------
    hoverScale: 1.1,
    pressScale: 0.95,
    /** 挂载：scale(0) → 1，同时 opacity 0 → 1 */
    mountScale: 0,
    mountOpacity: 0,
    /** 过渡：transform 走弹簧，opacity/box-shadow 走 ease-out，时长同为 dur-slow */
    transitionDurToken: '--lc-dur-slow',
    transformEaseToken: '--lc-ease-spring',
    fadeEaseToken: '--lc-ease-out',

    // ---- 未读角标 ------------------------------------------------------
    badgeHeightPx: 20,
    badgeMinWidthPx: 20,
    badgePadHPx: 5,
    badgeFontSizePx: 11,
    badgeFontWeight: 600,
    /** `top: -4px; right: -4px` —— 负值即"探出按钮外框" */
    badgeOffsetPx: -4,
    badgeRadiusToken: '--lc-radius-full',
    badgeShadowToken: '--lc-shadow-1',
    /** 见表头偏离 1：不是 --lc-danger */
    badgeBgColor: '#ef4444',
    badgeTextColor: '#ffffff',
    /** 超过这个数渲染 `99+`（`${badgeMaxCount}+`），别各端各写一个 99 */
    badgeMaxCount: 99,
});

/**
 * 全量契约表。导出脚本按此对象生成 design-tokens.json 的 `components` 段，
 * key 顺序即此处字面顺序（幂等纪律：重跑输出逐字节一致）。
 */
export const COMPONENT_CONTRACT = Object.freeze({
    bubble: BUBBLE,
    list: LIST,
    typing: TYPING,
    header: HEADER,
    composer: COMPOSER,
    card: CARD,
    kbArticle: KB_ARTICLE,
    kb: KB,
    home: HOME,
    shell: SHELL,
    launcher: Object.freeze({ ...LAUNCHER, glyphs: LAUNCHER_GLYPHS }),
});

/**
 * 脏值 → 合法图标风格。**唯一归一出口**：widget 渲染、dash 预览、导出脚本、
 * 单测全走它。服务端也归一（PHP 侧同一张表），客户端再归一一次不是重复劳动 ——
 * 缓存里的旧配置、宿主 `laneChatCustomizations` 直传都可能越界，`clampLauncherSize`
 * 是同一条理由。
 *
 * @param {unknown} v 任意输入
 * @returns {'duotone'|'classic'}
 */
export function normalizeLauncherIconStyle(v) {
    const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
    return LAUNCHER.iconStyles.includes(s) ? s : LAUNCHER.defaultIconStyle;
}

/**
 * 取某个风格的图标数据（已归一）。
 *
 * @param {unknown} style 风格名，脏值回落默认
 * @returns {{grid:number,padTopPx:number,layers:ReadonlyArray<{role:string,opacity:number,d:string}>}}
 */
export function launcherGlyph(style) {
    return LAUNCHER_GLYPHS[normalizeLauncherIconStyle(style)];
}

/**
 * 启动器的 CSS `transition` 值。生成而非手写 —— 手写的那一刻表和实现就分家了
 * （与 `kbArticleCssText` 同一条理由）。
 *
 * @param {string[]} props 参与过渡的属性；transform 走弹簧曲线，其余走 ease-out
 * @returns {string} 可直接放进 inline style 的 transition 值
 */
export function launcherTransition(props = ['transform', 'box-shadow', 'opacity']) {
    const d = `var(${LAUNCHER.transitionDurToken})`;
    return props
        .map((p) => {
            const ease = p === 'transform' ? LAUNCHER.transformEaseToken : LAUNCHER.fadeEaseToken;
            return `${p} ${d} var(${ease})`;
        })
        .join(', ');
}

// ---------------------------------------------------------------------------
// KB 正文 CSS 生成（web 侧落地；native 侧照 KB_ARTICLE 自行拼等价 CSS）
// ---------------------------------------------------------------------------

/**
 * 把 KB_ARTICLE 表渲染成一段作用域 CSS。
 *
 * 为什么生成而不是手写：手写的那一刻表和实现就分家了 —— 审计里 Android 的
 * KDoc 写 `10×14`、代码是 `12×10`，iOS 注释写 180ms、代码是 200ms，全是抄写
 * 导致的漂移。这里让 CSS **只能**从表里长出来。
 *
 * @param {string} scope 作用域选择器（web 用 `.lc-kb-article`）
 * @returns {string} CSS 文本
 */
export function kbArticleCssText(scope = '.lc-kb-article') {
    const a = KB_ARTICLE;
    const v = (token, fallback) => `var(${token}, ${fallback})`;
    return `
${scope} {
    font-size: ${a.bodySizePx}px;
    line-height: ${a.bodyLineHeight};
    color: ${v(a.bodyColorToken, '#1e293b')};
    word-wrap: break-word;
    overflow-wrap: anywhere;
}
${scope} > :first-child { margin-top: 0; }
${scope} > :last-child { margin-bottom: 0; }

${scope} h1 {
    font-size: ${a.h1SizePx}px;
    font-weight: ${a.h1Weight};
    line-height: ${a.h1LineHeight};
    margin: ${a.h1MarginTopPx}px 0 ${a.h1MarginBottomPx}px;
    color: ${v(a.bodyColorToken, '#1e293b')};
}
${scope} h2 {
    font-size: ${a.h2SizePx}px;
    font-weight: ${a.h2Weight};
    line-height: ${a.h2LineHeight};
    margin: ${a.h2MarginTopPx}px 0 ${a.h2MarginBottomPx}px;
    color: ${v(a.bodyColorToken, '#1e293b')};
}
${scope} h3, ${scope} h4, ${scope} h5, ${scope} h6 {
    font-size: ${a.h3SizePx}px;
    font-weight: ${a.h3Weight};
    line-height: ${a.h3LineHeight};
    margin: ${a.h3MarginTopPx}px 0 ${a.h3MarginBottomPx}px;
    color: ${v(a.bodyColorToken, '#1e293b')};
}

${scope} p { margin: 0 0 ${a.pMarginBottomPx}px; }
${scope} ul, ${scope} ol {
    margin: 0 0 ${a.pMarginBottomPx}px;
    padding-left: ${a.listIndentPx}px;
    list-style-position: outside;
}
${scope} li { margin: 0 0 ${a.listItemGapPx}px; display: list-item; }

${scope} a {
    color: ${v(a.linkColorToken, '#2563eb')};
    text-decoration: ${a.linkUnderline ? 'underline' : 'none'};
}

${scope} code {
    background: ${v(a.codeBgToken, '#f1f5f9')};
    border-radius: var(--lc-radius-xs);
    padding: 1px 4px;
    font-size: ${a.codeSizeEm}em;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
${scope} pre {
    background: ${v(a.codeBgToken, '#f1f5f9')};
    border-radius: ${a.codeRadiusPx}px;
    padding: ${a.codePaddingPx}px;
    margin: 0 0 ${a.pMarginBottomPx}px;
    overflow-x: auto;
}
${scope} pre code { background: none; padding: 0; font-size: 1em; }

${scope} blockquote {
    margin: 0 0 ${a.pMarginBottomPx}px;
    padding-left: ${a.blockquotePadHPx}px;
    border-left: ${a.blockquoteBarPx}px solid ${v('--lc-border', '#e5e7eb')};
    color: ${v(a.blockquoteColorToken, '#64748b')};
}

${scope} img {
    max-width: ${a.imageMaxWidthPct}%;
    height: auto;
    border-radius: ${a.imageRadiusPx}px;
    display: block;
}

${scope} table {
    width: 100%;
    border-collapse: collapse;
    margin: 0 0 ${a.pMarginBottomPx}px;
    font-size: ${a.tableFontSizePx}px;
}
${scope} th, ${scope} td {
    border: 1px solid ${v(a.tableBorderToken, '#e5e7eb')};
    padding: ${a.tableCellPadVPx}px ${a.tableCellPadHPx}px;
    text-align: left;
}
${scope} th { background: ${v('--lc-surface-2', '#f1f5f9')}; font-weight: 600; }

${scope} hr {
    border: 0;
    border-top: 1px solid ${v('--lc-border', '#e5e7eb')};
    margin: ${a.tagsMarginTopPx}px 0;
}
`;
}
